/**
 * Bronlar (§10): every tour operator's bookings and messages in one place. Operator texts
 * stay in their original language (Russian); the AI note and the summary are Uzbek.
 */
import { apiRequest, type Paginated } from "@/lib/api";

export type ConnectionState = "ok" | "failing" | "expired";

export interface OperatorConnection {
  id: number;
  operator_slug: string;
  operator_name: string;
  state: ConnectionState;
  last_success_at: string | null;
  last_error: string | null;
  bookings_count: number;
  unread_messages_count: number;
  debt: string | null;
  refund: string | null;
  currency: string | null;
  cabinet_url: string | null;
  supports_messages: boolean;
  supports_reply: boolean;
}

/** The list's row has the name and the mask; the rest comes with the booking's detail. */
export interface OperatorBookingTraveller {
  name: string;
  passport_masked: string | null;
  traveler_id?: number | null;
  can_view_passport?: boolean;
  /** The passport's spelling, only when it differs from the cabinet's. */
  passport_name?: string | null;
  passport_expiry?: string | null;
  /** The passport will not last six months past the trip. */
  expiry_soon?: boolean;
}

export interface BookingFlight {
  number: string;
  day: string;
  departs_at: string;
  departs_from: string;
  arrives_at: string;
  arrives_to: string;
  was_departs_at: string;
}

/** The cabinet's card. Empty until an adapter declaring `booking_detail` has read it. */
export interface BookingDetails {
  hotel?: string;
  room?: string;
  meal?: string;
  insurance?: string;
  extras?: string;
  outbound?: BookingFlight | null;
  inbound?: BookingFlight | null;
}

export interface OperatorBooking {
  id: number;
  operator_slug: string;
  operator_name: string;
  external_id: string;
  title_original: string;
  check_in: string | null;
  check_out: string | null;
  travellers: OperatorBookingTraveller[];
  /** The party size the cabinet states, also when it lists no names. */
  traveller_count: number;
  status: string;
  payment_status: string;
  /** `payment_status` in words; blank when the cabinet states nothing. */
  payment_status_label: string;
  amount_due: string | null;
  amount_paid: string | null;
  currency: string | null;
  deal_id: number | null;
  deal_number: string | number | null;
  client_id: number | null;
  client_name: string | null;
  deep_link: string | null;
  unread_count: number;
  has_important_alert: boolean;
  refund_amount: string | null;
  details: BookingDetails;
}

export type MessageCategory = "" | "flight_change" | "payment_due" | "cancellation" | "confirmation" | "refund" | "document";

export interface OperatorMessage {
  id: number;
  booking_id: number;
  operator_name: string;
  booking_external_id: string;
  topic: string;
  category: MessageCategory;
  respond_by: string | null;
  direction: string;
  sent_by_name: string;
  text_original: string;
  created_at: string;
  is_read: boolean;
  is_important: boolean;
  ai_summary: string | null;
  tourists_informed_by_name: string;
  tourists_informed_at: string | null;
}

/** A guardrailed draft for the client: the text, or the reason it was refused. */
export interface ClientDraft {
  text: string | null;
  blocked_reason: string | null;
  /** The thread the draft is waiting in; null when the client has none. */
  conversation_id: number | null;
  client_name: string | null;
}

export interface BookingFilters {
  operator?: string | undefined;
  status?: string | undefined;
  q?: string | undefined;
  departing_within?: 30 | undefined;
  new_messages?: 1 | undefined;
  branch?: number | undefined;
  deal?: number | undefined;
  client?: number | undefined;
}

export const bookingsApi = {
  connections: (branch?: number): Promise<OperatorConnection[]> =>
    apiRequest<OperatorConnection[]>("/api/tours/operator-connections/", { query: { branch } }),
  reconnect: (id: number, body: { username?: string; password?: string }): Promise<OperatorConnection> =>
    apiRequest<OperatorConnection>(`/api/tours/operator-connections/${String(id)}/reconnect/`, {
      method: "POST",
      body,
    }),
  testConnection: (id: number): Promise<{ ok: boolean; detail: string }> =>
    apiRequest<{ ok: boolean; detail: string }>(`/api/tours/operator-connections/${String(id)}/test/`, {
      method: "POST",
    }),
  list: (filters: BookingFilters): Promise<Paginated<OperatorBooking>> =>
    apiRequest<Paginated<OperatorBooking>>("/api/tours/operator-bookings/", {
      query: {
        operator: filters.operator,
        status: filters.status,
        q: filters.q,
        departing_within: filters.departing_within,
        new_messages: filters.new_messages,
        branch: filters.branch,
        deal: filters.deal,
        client: filters.client,
      },
    }),
  detail: (id: number): Promise<OperatorBooking> =>
    apiRequest<OperatorBooking>(`/api/tours/operator-bookings/${String(id)}/`),
  linkDeal: (bookingId: number, dealId: number): Promise<OperatorBooking> =>
    apiRequest<OperatorBooking>(`/api/tours/operator-bookings/${String(bookingId)}/link-deal/`, {
      method: "POST",
      body: { deal_id: dealId },
    }),
  messages: (bookingId: number): Promise<Paginated<OperatorMessage>> =>
    apiRequest<Paginated<OperatorMessage>>("/api/tours/operator-messages/", {
      query: { booking: bookingId },
    }),
  alerts: (branch?: number): Promise<Paginated<OperatorMessage>> =>
    apiRequest<Paginated<OperatorMessage>>("/api/tours/operator-messages/", { query: { alert: 1, branch } }),
  markRead: (messageId: number, isRead: boolean): Promise<OperatorMessage> =>
    apiRequest<OperatorMessage>(`/api/tours/operator-messages/${String(messageId)}/read/`, {
      method: "POST",
      body: { is_read: isRead },
    }),
  toFinance: (messageId: number): Promise<{ task_id: number; assignee_name: string }> =>
    apiRequest<{ task_id: number; assignee_name: string }>(
      `/api/tours/operator-messages/${String(messageId)}/to-finance/`,
      { method: "POST" },
    ),
  informed: (messageId: number): Promise<OperatorMessage> =>
    apiRequest<OperatorMessage>(`/api/tours/operator-messages/${String(messageId)}/informed/`, {
      method: "POST",
    }),
  clientDraft: (messageId: number): Promise<ClientDraft> =>
    apiRequest<ClientDraft>(`/api/tours/operator-messages/${String(messageId)}/client-draft/`, {
      method: "POST",
    }),
  translate: (text: string): Promise<{ text: string }> =>
    apiRequest<{ text: string }>("/api/tours/translate/", { method: "POST", body: { text, target: "ru" } }),
  reply: (bookingId: number, text: string, topic: string): Promise<OperatorMessage> =>
    apiRequest<OperatorMessage>(`/api/tours/operator-bookings/${String(bookingId)}/reply/`, {
      method: "POST",
      body: { text, topic },
    }),
};
